import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { trackStockClick, trackStockCompView } from '../../track';
import { commonH5Jump } from 'src/utils/jump';
import { getAppConfig, isWin, stockStatus, stockNumText, getDataType } from 'src/utils/common';
import { marketingApi } from 'src/api';
import { inApp } from '@hst/utils';
import { isDev } from 'src/config/env';

export interface IStockCard {
	recommendReason: string; // "zkmtest"
	relatedStock: {
		key: string; // "00700.HK"
		label: string; // "腾讯控股 • 00700.HK"
		value: string; // "00700.HK"
	};
	stockDesc: number; // 3
	stockSector: Array<{
		label: string; // "测试是否可"
		value: number; // 3149
		key: number; // 3149
	}>;
	dataType: string;
	ftName: string;
}

interface IStockCardItem {
	data: IStockCard;
}

export default (props: IStockCardItem) => {
	const {
		relatedStock,
		recommendReason,
		stockSector: stockPlate = [],
		dataType,
		ftName,
		stockDesc,
	} = props.data;
	const stockName = relatedStock.label.split('•')[0].trim();
	const stockCode = relatedStock.value;
	const searchParams = new URLSearchParams(useLocation().search);
	const pageId = searchParams.get('id') || '';
	const [stockHqData, setStockHqData] = useState<any>({});
	const { i18n, t } = useTranslation();
	const lang = i18n.language;

	useEffect(() => {
		trackStockCompView({
			stock_components_name: stockName,
			page_id: pageId || '',
			stock_components_code: stockCode,
		});
	}, []);

	const getHqData = async () => {
		const stockInfo = [{ dataType, securityCode: stockCode, name: stockName, ftName }];
		if (isDev) {
			setStockHqData({
				statusDescription: '正常交易',
				dataType: '30000',
				isRealTime: '1',
				name: '中国平安',
				securityCode: '601318.SH',
				marketType: 'SSE',
				lastPrice: '51.86',
				highAndLow: '0.24',
				highAndLowLimited: '0.46',
				status: '0',
			});
		} else {
			const res = await marketingApi.getStocksBySecurityCodes({
				securityCodes: JSON.stringify(stockInfo),
			});
			if (res.success) {
				const stockHqData = res.data.result?.[0] || {};
				setStockHqData(stockHqData);
			}
		}
	};

	useEffect(() => {
		getHqData();
	}, []);

	const btnClick = async (type?: string, plat?: any) => {
		let stockPlateDataType = '';
		let stockPlateName = '';
		let stockPlateCode = '';
		if (type === 'stockPlate') {
			stockPlateCode = plat.plateCode;
			stockPlateName = plat.label;
			stockPlateDataType = getDataType(plat.category, plat.marketType);
		}
		let source = {};
		if (inApp()) {
			const sourceRes = await getAppConfig();
			source = sourceRes;
		}
		const _scnl = searchParams.get('_scnl') || '';
		trackStockClick({
			stock_components_name: type === 'stockPlate' ? stockPlateName : stockName,
			page_id: pageId || '',
			stock_components_code: type === 'stockPlate' ? stockPlateCode : stockCode,
		});
		if (isWin()) {
			const innerDataType = type === 'stockPlate' ? stockPlateDataType : dataType;
			const innerStockCode = type === 'stockPlate' ? stockPlateCode : stockCode;
			// eslint-disable-next-line no-undef
			// @ts-ignore
			stockDetailFun && stockDetailFun(innerDataType, innerStockCode);
		} else {
			commonH5Jump(
				'3',
				{
					dataType: type === 'stockPlate' ? stockPlateDataType : dataType,
					source,
					stockName: type === 'stockPlate' ? stockPlateName : stockName,
					stockCode: type === 'stockPlate' ? stockPlateCode : stockCode,
					_scnl,
				},
				lang,
			);
		}
	};

	/** 关联的股票板块 */
	const stockPlateList = useMemo(
		() => (stockPlate instanceof Array ? stockPlate : []),
		[stockPlate],
	);

	/** 卡片样式类型 */
	const cardTypes = useMemo(() => {
		const res = [];
		if (stockPlateList.length > 0) {
			res.push('recommend');
		}
		if (recommendReason) {
			res.push('recommendReason');
		}
		return res;
	}, [recommendReason, stockPlateList]);

	return (
		<div className='stock-card'>
			<img src={require('./cardBg.png')} alt='stock-card' className='stock-card-bg' />
			<div className='stock-box'>
				<div className='stock-name'>
					<p className='stock-name-text'>{stockName}</p>
				</div>
				<div className='stock-code'>
					<p className='stock-code-text'>{relatedStock.value}</p>
					<div className={`stock-changes stock-${stockStatus(stockHqData)}`}>
						{stockNumText(stockHqData)}
					</div>
				</div>
				{cardTypes.length === 0 ? null : (
					<div className='stock-footer'>
						{/* 是有推荐理由 */}
						{stockDesc === 2 && cardTypes.includes('recommendReason') ? (
							<p className='stock-recommendReason'>
								{t('recommended')}：{recommendReason}
							</p>
						) : null}

						{/* 是否有股票板块 */}
						{stockDesc === 3 && cardTypes.includes('recommend') ? (
							<div className='stock-recommend'>
								<div className='stock-recommend-text'>{recommendReason || '概念推荐'}</div>
								{stockPlateList.map((plat) => (
									<div
										key={plat.key}
										className='stock-recommend-tag'
										onClick={() => btnClick('stockPlate', plat)}>
										<svg
											xmlns='http://www.w3.org/2000/svg'
											className='stock-recommend-tag-icon'
											viewBox='0 0 12 12'
											fill='none'>
											<g id='icon_btn_thumbs_up'>
												<path
													id='Subtract'
													fill-rule='evenodd'
													clip-rule='evenodd'
													d='M5.5707 1.25C5.37386 1.25 5.19534 1.36548 5.11465 1.54501L3.54918 5.02781L2.74307 5.02783C1.96171 5.01667 1.35036 5.64851 1.2545 6.35792C1.2515 6.38012 1.25 6.40248 1.25 6.42487V9.39985C1.25 9.42224 1.2515 9.44461 1.2545 9.4668C1.35486 10.2096 1.99384 10.7607 2.74309 10.7498L8.80304 10.7499C9.47452 10.756 10.0479 10.2669 10.1488 9.6031L10.1489 9.60231L10.7347 5.77742C10.7941 5.38602 10.6787 4.98817 10.4191 4.68928C10.1602 4.39118 9.78371 4.22142 9.38884 4.22498H7.34425V3.02499C7.34425 2.04523 6.55076 1.25 5.5707 1.25Z'
													fill='#F24822'
												/>
											</g>
										</svg>
										<span className='stock-recomend-tag-text'>{plat.label}</span>
									</div>
								))}
							</div>
						) : null}
					</div>
				)}
			</div>
			<a className='stock-card--view' href='javascript:;' onClick={() => btnClick()}>
				{t('checkTheMarket')}&gt;
			</a>
		</div>
	);
};
